import { useNavigation } from "@react-navigation/native";
import { Check } from "@tamagui/lucide-icons";
import { Timestamp } from "firebase/firestore";
import { observer } from "mobx-react-lite";
import React from "react";
import { ActivityIndicator, Linking } from "react-native";
import { View, XStack, YStack } from "tamagui";
import { showConfetti } from "../../core/states.ts/showConfetti";
import { useStores } from "../../core/stores";
import { Body } from "../../ui/Body";
import { Box } from "../../ui/Box";
import { Button } from "../../ui/Button";
import { Screen } from "../../ui/Screen";
import { Separator } from "../../ui/Separator";
import { Subtext } from "../../ui/Subtext";
import { Title } from "../../ui/Title";
import { AvatarSize, UserPropsAvatar } from "../../ui/UserAvatar";
import { UserAvatarGroupRaw } from "../../ui/UserAvatarGroupRaw";
import type { BusinessCustomerData } from "../admin/customers/BusinessCustomerModel";
import { useCreateBusinessPlanFromTemplate } from "../admin/templates/useCreateBusinessPlanFromTemplate";
import { BusinessFeedEventType } from "../feed/BusinessFeedItem";
import type { FeedItemUserIntroData } from "../feed/events/FeedItemUserIntro";
import {
  type BusinessStaticUserInfoWithUserUid,
  BusinessType,
} from "./BusinessPlanModel";

const mockedDemoCustomer: BusinessCustomerData = {
  uid: "dummy-demo-uid",
  applying: {},
  superUserInfo: {},
  profileQuestions: [
    {
      uid: "aad200d6-a184-4032-8e75-88f03c803054",
      question: "What’s the highest altitude you’ve been to before?",
    },
    {
      uid: "73a379db-fb74-4954-bae2-8475a87f63d8",
      question: "Where are you from?",
    },
    {
      uid: "099b7517-5f29-4030-a24b-895ca09afbd4",
      question: "One thing you’re looking forward to on this trip?",
    },
  ],
  profile: {
    name: "DemoCompany",
    theme: null,
    introText: null,
    logoUrl:
      "https://firebasestorage.googleapis.com/v0/b/planira-1337.appspot.com/o/media%2FFpkV9uH5jdOwvJazn03V%2F882d094e-4c6c-492a-bf9b-d9adf6ac23f4?alt=media&token=00093709-df34-4d7b-b0fb-665c4d2918d7",
  },
};

const mockedDemoTitle = "Kilimanjaro (Demo)";

// TODO - pick one for prod
const mockedDemoTemplateUid = __DEV__
  ? "gRYcWBEeTOnvwJ4xIeSc"
  : "ovIyVkOrJuF6MKiQcWxv";

const mockedParticipants: {
  avatarUrl: string;
  displayName: string;
  email: string;
  authedUsers: string[];
}[] = [
  {
    avatarUrl:
      "https://firebasestorage.googleapis.com/v0/b/planira-1337.appspot.com/o/avatars%2FuKE2tJjZwhRUZZee0jdWgCTwmp33?alt=media&token=22ad2b78-aa20-4637-a74c-f7d1235df545",
    displayName: "Isabella Rossi",
    email: "isabella@planira.app",
    authedUsers: [],
  },
  {
    avatarUrl:
      "https://firebasestorage.googleapis.com/v0/b/planira-1337.appspot.com/o/avatars%2FnzCizIE3HKSs4RtGSQsGeUUGqQF3?alt=media&token=f06ebeb0-9d54-4306-897e-0f6a03cf4fe8",
    displayName: "Tomás García",
    email: "Tomás@planira.app",
    authedUsers: [],
  },
  {
    avatarUrl:
      "https://firebasestorage.googleapis.com/v0/b/planira-1337.appspot.com/o/avatars%2FM14JXtXQ5xSxk85fAHAXeIbtk3U2?alt=media&token=979ae066-9a25-4ed8-8d56-e7571589a1d3",
    displayName: "Michael Johnson",
    email: "Michael@planira.app",
    authedUsers: [],
  },
  {
    avatarUrl:
      "https://firebasestorage.googleapis.com/v0/b/planira-1337.appspot.com/o/avatars%2FJPHkwPhDA3c7Oj9RcvK6QrvT9Qi1?alt=media&token=74773648-a1d8-4052-b005-6a1dbab6816c",
    displayName: "Chen Wei",
    email: "Chen@planira.app",
    authedUsers: [],
  },
  {
    avatarUrl:
      "https://firebasestorage.googleapis.com/v0/b/planira-1337.appspot.com/o/avatars%2FHNVouMUZxFYFvB0laF3zzii9BmJ3?alt=media&token=26e8953c-11a7-46eb-bdd8-2613dada06d5",
    displayName: "Priya Singh",
    email: "Priya@planira.app",
    authedUsers: [],
  },
  {
    avatarUrl:
      "https://firebasestorage.googleapis.com/v0/b/planira-1337.appspot.com/o/avatars%2FPgq1GqiswjQw0mcCjeizUBwXgp32?alt=media&token=fe79c267-cf20-4250-9f2b-091138feae3c",
    displayName: "Alex Martinez",
    email: "Alex@planira.app",
    authedUsers: [],
  },
];

// @ts-expect-error not synced
const mockedUserInfos: BusinessStaticUserInfoWithUserUid[] =
  mockedParticipants.map((u, i) => ({
    ...u,
    userUid: i,
  }));

const introText =
  "Hi everyone!\n" +
  "\n" +
  "👋 I’m Isabella, your guide for this Kilimanjaro adventure!\n" +
  "\n" +
  "I’ve led treks around the world for over 8 years, and I’m here to make sure our journey is safe, fun, and unforgettable. \n" +
  "\n" +
  "On this trip, I’ll help you:\n" +
  "Pace yourself for the climb 🥾\n" +
  "Stay safe and comfortable at altitude ⛰️\n" +
  "Enjoy every step and celebrate the journey 🎉\n" +
  "\n" +
  "I love sharing stories about the landscapes and local culture, and I can’t wait to get to know each of you. Feel free to ask me anything — gear tips, trail advice, or just a chat around the campfire. \n" +
  "\n" +
  "Let’s make this an adventure to remember!";

export const BusinessPlanDemoScreen = observer(() => {
  const navigation = useNavigation();
  const createPlanFromTemplate = useCreateBusinessPlanFromTemplate();

  const { sessionStore } = useStores();

  const [isCreating, setIsCreating] = React.useState<string | null>(null);

  const createDemo = React.useCallback(async () => {
    setIsCreating("Creating event...");

    const businessPlan = await createPlanFromTemplate(
      mockedDemoCustomer,
      mockedDemoTitle,
      mockedDemoTemplateUid,
    );

    setIsCreating("Setting event date...");

    // Update to demo
    await businessPlan.setType(BusinessType.Demo);

    const now = new Date();
    const oneYearFromNow = new Date(now.getTime() + 365 * 24 * 60 * 60 * 1000);
    await businessPlan.updateEventInfo({
      date: Timestamp.fromDate(oneYearFromNow),
    });

    setIsCreating("Adding yourself...");
    await businessPlan.addUser({
      displayName: "DemoUser",
      avatarUrl: "",
      email: "demo@planira.app",
      authedUsers: [sessionStore.uid],
    });

    setIsCreating("Adding participants...");
    const allUsers = await Promise.all(
      mockedParticipants.map((p) => businessPlan.addUser(p)),
    );

    const guide = allUsers[0];

    setIsCreating("Adding guide...");
    await businessPlan.toggleMemberRole(guide.uid, "guide");
    await guide.updateProfileData({
      profileQuestions: {
        "aad200d6-a184-4032-8e75-88f03c803054":
          "Around 5,900 meters — on previous Kilimanjaro and Andes expeditions.",
        "73a379db-fb74-4954-bae2-8475a87f63d8":
          "I’m based in Moshi, Tanzania, right at the foot of Kilimanjaro.",
        "099b7517-5f29-4030-a24b-895ca09afbd4":
          "Meeting all of you, building a strong team spirit, and guiding everyone safely to the summit!",
      },
      description: introText,
    });

    setIsCreating("Adding feed events...");

    await businessPlan.addToFeed(
      {
        type: BusinessFeedEventType.UserCompletedIntro,
        moduleUid: "",
        itemData: {
          authorUid: guide.uid,
        } satisfies FeedItemUserIntroData,
      },
      true,
    );

    showConfetti();
    setIsCreating("All done!");

    setTimeout(() => {
      setIsCreating("Redirecting...");
    }, 1000);
    setTimeout(() => {
      navigation.navigate("BusinessHome", {
        businessUid: businessPlan.uid,
      });
    }, 3000);
  }, [createPlanFromTemplate, sessionStore, navigation]);

  return (
    <Screen theme={null}>
      <YStack gap={"$medium"}>
        <Box>
          <Title>Demo time 🍿</Title>
          <Body>
            Nothing is better than testing out the product for yourself.{" "}
            <Body fontWeight={"bold"}>Great choice!</Body>
          </Body>

          <Separator />

          <Body>
            You will be presented with an imaginary trip - where you and your
            small group will climb the mighty Kilimanjaro mountain in Africa.
          </Body>

          <XStack gap="$medium">
            <YStack gap={"$small"}>
              <Subtext
                alignSelf="center"
                fontSize={10}
                fontWeight={"600"}
                letterSpacing={1.1}
              >
                YOU
              </Subtext>
              <UserPropsAvatar
                avatarUrl={null}
                size={AvatarSize.Medium}
                displayName={"DemoUser"}
              />
            </YStack>

            <YStack gap={"$small"}>
              <Subtext fontSize={10} fontWeight={"600"} letterSpacing={1.1}>
                YOUR GROUP
              </Subtext>
              <UserAvatarGroupRaw users={mockedUserInfos} />
            </YStack>
          </XStack>

          <Body>
            So your role in this demo, is to mimic as your end-users. Finding
            out what to do next, who you will be going with and much more.
          </Body>

          {!!isCreating && (
            <View height={250} alignItems="center" justifyContent="center">
              <YStack gap="$medium">
                <ActivityIndicator size={"large"} color={"#ffd84e"} />
                <Body muted>{isCreating}</Body>
              </YStack>
            </View>
          )}

          {isCreating === null && (
            <Button icon={Check} onPress={createDemo}>
              Create Demo
            </Button>
          )}

          <Body muted>
            Once created you will be moved to the demo. It's only yours. Try out
            everything you want to. Talk soon!
          </Body>
        </Box>
      </YStack>

      <Separator />

      <View marginHorizontal={"$large"}>
        <Body muted>
          This is a demo page illustrating the user-flow of Planira Business.{" "}
        </Body>
        <Body
          muted
          cursor={"pointer"}
          textDecorationLine={"underline"}
          onPress={() => {
            Linking.openURL("https://planira.app/business");
          }}
        >
          Read more about it here.
        </Body>
      </View>
    </Screen>
  );
});

export default BusinessPlanDemoScreen;
